import '../../styles/docs.scss';
import {useQuery} from '@tanstack/react-query';
import UserContext from "../contexts/user";
import {set, omit} from 'lodash';
import React, {useContext, useState} from 'react';
import {TEVService} from '../tevapi/io';
import {Card, Input, Popover, Space, Table, TableColumnType, Tabs, Typography} from 'antd';
import {IAliasedItem, ICollectionOverview, IColumnList, IItemColumn, ITableResponse} from '../tevapi/interfaces';
import {TablePaginationConfig, TableProps} from 'antd/es/table';
import {TEVItemField} from './data-rendering';
import {ColumnControl, IColumnFilter, IFilterControl} from './table-controls/column-filter';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {TableMenu} from './table-controls/menu';
import {VettingFormModal} from './table-selection/select-by-vetting';
import {FilterValue, SorterResult, TableCurrentDataSource} from 'antd/es/table/interface';
import {TablePaginator} from './table-controls/pagination';
import {useDebounce} from 'use-debounce';


const ORDERABLE_DATATYPES = [
  "integer",
  "decimal",
  "number",
  "datetime",
  "string"
];

interface ITableQueryParameters {
  page: number;
  page_size: number;
  filter_string: string;
  ordering: string;
};

interface IOrderSpec {
  column_pk: number;
  order_how: string;
};


const itemColumnToFilterDropdown = (columnFilter: IColumnFilter): TableColumnType => {
  return {
    filterDropdown: () => (
      <ColumnControl {...columnFilter} />
      ),
  };
};

const defaultCellRender = (column) => {
  const func = (v, i) => {
    return (
        <TEVItemField
          value={v}
          key={i}
          row={{
            title: column.name,
            field: column.field_path,
            type: column.datatype,
            readonly: true,
          }}
        />
    );
  };
  return func;
};

const firstCellRender = (column) => {
  const func = (v, i) => {
    return (
      <a href={`/items/${i.pk}/`} target="_blank">
        <TEVItemField
          value={v}
          key={i}
          row={{
            title: column.name,
            field: column.field_path,
            type: column.datatype,
            readonly: true,
          }}
        />
      </a>
    );
  };
  return func;
};

const filterIconHOC = (column: IItemColumn, orderState: IOrderSpec[]) => {
  if (!ORDERABLE_DATATYPES.includes(column.datatype)) {
    return () => null;
  }
  const pos = orderState.findIndex(
    (value) => value.column_pk === column.pk
  );

  return ({sortOrder}) => {
    if (pos < 0) {
        return <FontAwesomeIcon icon="fa-duotone fa-solid fa-sort" />;
    } else {
        return (
          <Space size="small">
            {sortOrder == "ascend" ?
              <FontAwesomeIcon icon="fa-duotone fa-solid fa-sort-up" /> :
              <FontAwesomeIcon icon="fa-duotone fa-solid fa-sort-down" />
            }
          </Space>
        )
    }
  };
};

const itemColumnsToAntDColumns = (
  column_list: IColumnList,
  filterState: {[key: number]: string},
  filterControl: IFilterControl,
  orderState: IOrderSpec[],
) => {

  const user = useContext(UserContext);
  const antdColumns: TableProps<IAliasedItem>['columns'] = column_list.columns.map((col, ithColumn) => ({
    title: (
      <Space> <Typography.Text>{col.name}</Typography.Text>
        <Popover
          content={
            <Card
              title={
                user.is_staff ?
                <a
                  href={`/admin/itemdata/itemcolumn/${col.pk}/change/`}
                  target='_blank'
                >
                  {col.name}
                </a>
                :
                  col.name
              }
              extra={
                <Space>
                  <Typography.Text code>
                    {col.datatype}
                  </Typography.Text>
                </Space>
              }
              size="small"
              className='column-help-card'
            >
              <div className="respect-newlines">
                {col.description}
              </div>
            </Card>
          }
        >
          <FontAwesomeIcon icon="fa-regular fa-square-question" />
        </Popover>
      </Space>
    ),
    key: col.pk,
    dataIndex: col.name,
    minWidth: Math.max(col.name.length * 15, 9 * 15),
    render: ithColumn == 0 ? firstCellRender(col) : defaultCellRender(col),
    filtered: filterState[col.pk] != undefined,
    ...itemColumnToFilterDropdown({
      column: col,
      filters: filterState,
      ...filterControl
    }),
    sorter: ORDERABLE_DATATYPES.includes(col.datatype) ? {multiple: ithColumn} : false,
    sortIcon: filterIconHOC(col, orderState),
  }));
  return antdColumns;
};


interface IDataTable {
  collection: ICollectionOverview;
};

export const DataTable: React.FC<IDataTable> = ({collection}) => {
  const [rawFilters, setRawFilters] = useState({});
  const [rawOrders, setRawOrders] = useState<IOrderSpec[]>([]);
  const [vettingSelectModal, setVettingSelectModal] = useState(false);

  const user = useContext(UserContext);

  const [queryParams, setQueryParams] = useState<ITableQueryParameters>({
    page: 1,
    page_size: 50,
    filter_string: btoa(JSON.stringify(rawFilters)),
    ordering: btoa(JSON.stringify(rawOrders)),
  });

  const [debouncedQuery] = useDebounce(queryParams, 300);

  const [selection, setSelection] = useState<Array<number>>([]);

  // Define some handy nested query parameter setter functions
  const setFilterFor = (path: string, value: any): void => {
    const shallowCopy = {...rawFilters};
    const queryParamCopy = {...queryParams}
    set(shallowCopy, path, value);
    setRawFilters(shallowCopy);
    queryParamCopy.filter_string = btoa(JSON.stringify(shallowCopy));
    setQueryParams(queryParamCopy);
  };

  const resetFilterFor = (path: string): void => {
    const shallowCopy = omit({...rawFilters}, path);
    const queryParamCopy = {...queryParams}

    queryParamCopy.filter_string = btoa(JSON.stringify(shallowCopy));
    setRawFilters(shallowCopy);
    setQueryParams(queryParamCopy);
  };

  const tableQuery = useQuery({
    queryKey: ["table", collection.pk, debouncedQuery],
    queryFn: async (): Promise<ITableResponse> => {
      const response = await TEVService.get(
        `/collections/${collection.pk}/items/`,
        {params: queryParams}
      );
      return response.data;
    },
  });

  const onSelect = (record) => {
    const copy = [...selection];
    copy.push(record.pk);
    setSelection(copy);
  };

  const onSelectAll = (selected: boolean) => {
    if (selected) {
      TEVService.get(
        `/collections/${collection.pk}/select/`,
        {params: queryParams}
      ).then(
        (response) => setSelection(response.data)
      );
    } else {
      setSelection([]);
    }
  };

  const onSelectNone = () => {
    setSelection([]);
  };

  // Define some handy functions to handle selection

  if (tableQuery.isError) {
    return <Typography.Text code>{JSON.stringify(tableQuery)}</Typography.Text>
  }

  const rowSelection = {
    selectedRowKeys: selection,
    onChange: (keys: Array<number>, _, __) => setSelection(keys),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_NONE,
      {
        key: "vetting",
        text: "By Vetting",
        onSelect: (
          () => setVettingSelectModal(true)
        ),
      },
    ],
    onSelect,
    onSelectAll,
    onSelectNone,
  };

  const handleSortChange = (sorterArgs: SorterResult<IAliasedItem> | SorterResult<IAliasedItem>[]) => {
    const queryParamCopy: ITableQueryParameters = {...queryParams};
    const orderSpec: IOrderSpec[] = [];
    if ("columnKey" in sorterArgs) {
      // Single order spec
      if (sorterArgs.columnKey === undefined) {
        // Unset order, noop
      } else {
        orderSpec.push({
          column_pk: sorterArgs.columnKey,
          order_how: sorterArgs.order === "ascend" ? "asc" : "desc",
        });
      }
    } else {
      orderSpec.push(...sorterArgs.map(
        (spec) => ({
          column_pk: spec.columnKey,
          order_how: spec.order === "ascend" ? "asc" : "desc",
        })
      ));
    }
    queryParamCopy.ordering = btoa(JSON.stringify(orderSpec));
    setRawOrders(orderSpec);
    setQueryParams(queryParamCopy);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IAliasedItem> | SorterResult<IAliasedItem>[],
    extra: TableCurrentDataSource<IAliasedItem>
  ) => {
    if (extra.action === "sort") {
      handleSortChange(sorter);
      return;
    }
    return;
  };

  const dataSource = tableQuery.data?.items || [];
  const columns = itemColumnsToAntDColumns(
    collection.columns,
    rawFilters,
    {setFilterFor, resetFilterFor, setSelectedKeys: console.log},
    rawOrders,
  )
  const width = columns.reduce((sum, c) => sum + (c.minWidth ?? 0), 0);

  const tabItems = [
    {
      label: (
        <Space>
          <FontAwesomeIcon icon="fa-regular fa-table" />
          Table
        </Space>
      ),
      key: "table",
      children: (
        <>
          <Space direction='vertical'>
            <Space direction='horizontal'>
              <Input.Search
                placeholder="Table wide search"
                onSearch={(value) => setFilterFor("tablewide_filter", value)}
                allowClear
                enterButton
              />
              <TableMenu collection={collection} selection={selection} />
            </Space>
            <div style={{maxWidth: "100vw", overflowX: "scroll"}}>
              <Table
                bordered
                columns={columns}
                rowKey="pk"
                size="small"
                scroll={{
                  x: width,
                  y: 400,
                }}
                tableLayout="fixed"
                loading={tableQuery.isLoading}
                pagination={false}
                dataSource={dataSource}
                footer={() => (
                  `Showing ${tableQuery.data?.items.length} out of ${tableQuery.data?.total_items} total items`
                )}
                rowSelection={rowSelection}
                onChange={handleTableChange}
              />
            </div>
            <TablePaginator
              queryParams={queryParams}
              setQueryParams={setQueryParams}
              total={tableQuery.data?.total_items}
            />
          </Space>
        </>
      ),
    },
    {
      label: (
        <Space>
          <FontAwesomeIcon icon="fa-regular fa-chart-scatter" />
          Graph
        </Space>
      ),
      key: "graph",
      disabled: true,
    }
  ];

  if (user.is_staff) {
    tabItems.push({
      label: (
        <Space>
          <FontAwesomeIcon icon="fa-solid fa-database" />
          Explain
        </Space>
      ),
      key: "explain",
      children: (
        <div className="respect-newlines">
          {tableQuery.data?.explain}
        </div>
      ),
    });
  }

  return (
    <>
      <VettingFormModal
        collectionPK={collection.pk}
        isOpen={vettingSelectModal}
        setIsOpen={setVettingSelectModal}
        selection={selection}
        setSelection={setSelection}
      />
      <Tabs defaultActiveKey='table' items={tabItems} />
    </>
  );
};
